import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';

import { LocaleService, BCP47Locale } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { LabResult } from './models/labresult.model';
import { clearLabresultResult, getLabresults } from './store/actions/labresult.actions';
import {
  getLabresultTotalNumberOfItems,
  selectLabresults,
} from './store/selectors/labresult.selectors';
import { SiteState } from '../shared/models/siteState.model';

@Component({
  selector: 'app-labresult',
  templateUrl: './labresult.component.html',
  styleUrls: ['./labresult.component.scss'],
  standalone: false,
})
export class LabresultComponent implements OnInit, OnDestroy {
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  private readonly store = inject(Store<SiteState>);
  private readonly localeService = inject(LocaleService);
  private readonly route = inject(ActivatedRoute);

  locale: BCP47Locale;
  dataSource: MatTableDataSource<LabResult> = new MatTableDataSource();
  pageTitle = toSignal(this.store.select(SiteSelectors.selectPageTitle));
  keyword: string;
  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50];
  currentPage = 1;
  currentPageSize = 10;
  totalNumberOfItems = toSignal(this.store.select(getLabresultTotalNumberOfItems));

  form = this.formBuilder.group({
    search: [],
  });

  displayedColumnLabResultTable: string[] = [
    'labResultId',
    'description',
    'labId',
    'updated',
    'statusDescription',
    'reason',
  ];

  subscription: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private location: Location,
  ) {
    this.locale = this.localeService.getLocale();
    effect(() => {
      const titleKey = this.route.snapshot.data['pageTitle'];
      if (titleKey) {
        this.store.dispatch(
          SiteActions.updatePageTitle({ pageTitle: this.localeService.translate(titleKey) }),
        );
      }
    });
  }

  ngOnInit(): void {
    this.keyword = this.activatedRoute.snapshot.queryParamMap.get('keyword');

    if (this.keyword) {
      this.searchLabresults(true);
      this.form.controls.search.setValue(this.keyword);
    }

    this.subscription = this.store.select(selectLabresults).subscribe((labresults) => {
      if (labresults) {
        this.dataSource.data = labresults;
      }
    });

    this.subscription.add(
      this.sort.sortChange.subscribe(() => {
        const direction = this.sort.direction || 'desc';
        if (this.sort.direction === 'desc') {
          this.sort.direction = 'asc';
        }

        this.sort.direction = direction;
        this.searchLabresults(true);
        this.currentPage = 1;
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.currentPage = 1;
  }

  searchLabresults(resetPaginator: boolean) {
    if (resetPaginator === true) {
      this.resetPaginator();
      this.sort.start = 'desc';
    }

    this.store.dispatch(
      getLabresults({
        keyword: this.keyword,
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        sort: this.sort.direction,
      }),
    );
    const url = this.router
      .createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: { keyword: this.keyword },
        queryParamsHandling: 'merge',
      })
      .toString();
    this.location.go(url);
  }

  clearSearchbox() {
    this.form.controls.search.setValue('');
    this.store.dispatch(clearLabresultResult());
  }

  onPage(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.currentPageSize = event.pageSize;
    this.searchLabresults(false);
  }
}
